import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BarChart3, Database, LoaderCircle, Network, Table2 } from "lucide-react";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "@/lib/utils";
import DataProfileStrip from "@/frontend/features/Fde/data/DataProfileStrip";
import DataFilterBar from "@/frontend/features/Fde/data/DataFilterBar";
import DatasetPicker from "@/frontend/features/Fde/data/DatasetPicker";
import SemanticObjectDataExplorer from "@/frontend/features/Fde/data/SemanticObjectDataExplorer";
import useDataPreview from "@/frontend/features/Fde/data/useDataPreview";
import type { DataSourceFilter, ForgeDataPayload, ForgeAnalyticsModel, ForgeQueryFilter, SemanticLayer } from "@/frontend/features/Fde/data/types";
import { useWorkstationPageContext } from "@/frontend/features/AgentChats/workstationPageContext";

const GraphicWalkerStudio = lazy(() => import("@/frontend/features/Fde/data/GraphicWalkerStudio"));
const DataTableGrid = lazy(() => import("@/frontend/features/Fde/data/DataTableGrid"));

interface Props {
  projectId?: string | null;
  analytics: ForgeDataPayload["analytics"];
  semanticLayer?: SemanticLayer;
  copy: Record<string, string>;
}

type View = "table" | "visual";

function initialModel(models: ForgeAnalyticsModel[]) {
  return models.find((model) => model.name.startsWith("mart_") && model.name.includes("daily"))
    ?? models.find((model) => model.visibility === "primary")
    ?? models.find((model) => model.name.startsWith("mart_"))
    ?? models[0];
}

export default function DataExplorer({ projectId, analytics, semanticLayer, copy }: Props) {
  const hasSemanticObjects = Boolean(projectId && semanticLayer?.object_types.some((item) => item.can_hydrate));
  const [scope, setScope] = useState<"objects" | "sources">(() => {
    const requested = typeof window === "undefined" ? null : new URLSearchParams(window.location.search).get("data_view");
    return requested === "sources" || !hasSemanticObjects ? "sources" : "objects";
  });
  const defaultModel = useMemo(() => initialModel(analytics.models), [analytics.models]);
  const [selectedName, setSelectedName] = useState(defaultModel?.name ?? "");
  const [view, setView] = useState<View>("table");
  const [filters, setFilters] = useState<DataSourceFilter[]>([]);
  const [compiledFilters, setCompiledFilters] = useState<ForgeQueryFilter[]>([]);
  const [search, setSearch] = useState("");
  const selected = analytics.models.find((model) => model.name === selectedName) ?? defaultModel;
  const fieldNames = selected?.fields.slice(0, 40).map((field) => field.name) ?? [];
  const { result, error } = useDataPreview(projectId, selected?.name, fieldNames, compiledFilters, copy.preview_error);
  const visibleRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("pt-BR");
    if (!result || !query) return result?.rows ?? [];
    return result.rows.filter((row) => Object.values(row).some((value) => String(value ?? "").toLocaleLowerCase("pt-BR").includes(query)));
  }, [result, search]);

  useWorkstationPageContext("forge-data-explorer", {
    surface: "forge",
    section: "data",
    view: scope === "objects" ? "semantic_objects" : view,
    project: projectId ? { id: projectId } : undefined,
    dataset: selected ? {
      model: selected.name,
      label: selected.label,
      layer: selected.layer,
      connection_id: selected.connection_id,
      source_row_count: result?.source_row_count ?? selected.row_count,
      preview_row_count: result?.rows.length ?? 0,
      visible_row_count: visibleRows.length,
      fields: selected.fields.map((field) => field.name).slice(0, 80),
    } : undefined,
    filters: compiledFilters,
    selection: search.trim() ? { text_search: search.trim() } : undefined,
  });

  useEffect(() => {
    if (selected?.name && selected.name !== selectedName) setSelectedName(selected.name);
    if (!selected && selectedName) setSelectedName("");
  }, [selected, selectedName]);

  if (scope === "sources" && (analytics.error || analytics.models.length === 0)) {
    return <ExplorerEmptyState analytics={analytics} copy={copy} />;
  }

  if (scope === "objects" && projectId && semanticLayer) {
    return <div className="flex h-full min-h-0 flex-col">
      <ScopeNavigation scope={scope} setScope={setScope} copy={copy} />
      <div className="min-h-0 flex-1"><SemanticObjectDataExplorer projectId={projectId} layer={semanticLayer} copy={copy} /></div>
    </div>;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-background">
      {hasSemanticObjects && <ScopeNavigation scope={scope} setScope={setScope} copy={copy} />}
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-3 py-2.5">
        <DatasetPicker models={analytics.models} selected={selected} onSelect={(name) => { setSelectedName(name); setFilters([]); setCompiledFilters([]); setSearch(""); }} copy={copy} />
        <div className="min-w-56 flex-1">
          <DataFilterBar fields={selected?.fields ?? []} filters={filters} search={search} onSearchChange={setSearch} onApply={(draft, compiled) => { setFilters(draft); setCompiledFilters(compiled); }} copy={copy} />
        </div>
        <div className="flex rounded-lg border border-border bg-muted/25 p-0.5">
          <ViewButton active={view === "table"} onClick={() => setView("table")} icon={Table2} label={copy.table_view} />
          <ViewButton active={view === "visual"} onClick={() => setView("visual")} icon={BarChart3} label={copy.visual_view} />
        </div>
      </header>
      {!result && !error && <Loading label={copy.loading_preview} />}
      {error && <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-destructive">{error}</div>}
      {result && view === "table" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <DataProfileStrip profiles={result.profiles} sourceRowCount={result.source_row_count} sourceLabel={compiledFilters.length ? copy.filtered_source : copy.full_source} copy={copy} />
          <div className="min-h-0 flex-1">
            <Suspense fallback={<div className="h-full animate-pulse bg-muted/20" />}>
              <DataTableGrid columns={result.columns} fields={result.fields} rows={visibleRows} />
            </Suspense>
          </div>
        </div>
      )}
      {result && view === "visual" && (
        <div className="min-h-0 flex-1 overflow-hidden">
          <ErrorBoundary resetKeys={[result.model]} fallbackRender={() => <Loading label={copy.visualization_error} destructive />}>
            <Suspense fallback={<div className="h-full animate-pulse bg-muted/30" />}>
              <GraphicWalkerStudio fields={result.fields} rows={visibleRows} model={result.model} projectId={projectId!} errorLabel={copy.preview_error} />
            </Suspense>
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
}

function ScopeNavigation({ scope, setScope, copy }: { scope: "objects" | "sources"; setScope: (scope: "objects" | "sources") => void; copy: Record<string, string> }) {
  return <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border px-3"><ViewButton active={scope === "objects"} onClick={() => setScope("objects")} icon={Network} label={copy.semantic_business_objects} /><ViewButton active={scope === "sources"} onClick={() => setScope("sources")} icon={Database} label={copy.semantic_technical_sources} /></div>;
}

function ViewButton({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof Table2; label: string }) {
  return (
    <button type="button" onClick={onClick} className={cn("flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors", active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>
      <Icon className="size-3.5" />{label}
    </button>
  );
}

function Loading({ label, destructive = false }: { label: string; destructive?: boolean }) {
  return <div className={cn("flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground", destructive && "text-destructive")}><LoaderCircle className="size-4 animate-spin" />{label}</div>;
}

function ExplorerEmptyState({ analytics, copy }: Pick<Props, "analytics" | "copy">) {
  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="max-w-lg rounded-2xl border border-border bg-muted/15 p-7 text-center shadow-sm">
        <span className="mx-auto flex size-11 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
          {analytics.error ? <AlertTriangle className="size-5" /> : <Database className="size-5" />}
        </span>
        <h3 className="mt-4 text-base font-semibold">{analytics.error ? copy.analytics_unavailable : copy.no_models}</h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{analytics.error ? copy.analytics_unavailable_description : copy.no_models_description}</p>
      </div>
    </div>
  );
}
